import { Fragment, useState } from "react";
import { ChartCard, type ChartCardState } from "../../ChartCard";
import { useInstancePluginUsage } from "../../../../hooks/useInstancePluginUsage";
import type { PluginFeatureUsage, TenantPluginUsage } from "../../../../hooks/useAdminStatus";
import { formatRelativeTime, formatDateTime } from "../../../../utils/formatDateTime";
import { rangeShortLabel, rangeWindowLabel, type InstanceRange } from "../../../../lib/instanceDetailRange";
import { LAST_PASS_NOTE, LAST_PASS_TITLE, emptyReason, lastPassTone, topFeature } from "./pluginUsageRows";
import styles from "./PluginUsagePanel.module.css";

type PluginUsagePanelProps = {
  readonly tenantId: string;
  readonly range: InstanceRange;
};

const compact = new Intl.NumberFormat(undefined, { notation: "compact", maximumFractionDigits: 1 });
const exact = new Intl.NumberFormat();

const tokens = (value: number): string => (value === 0 ? "0" : compact.format(value));

const when = (at: string): string => formatRelativeTime(at);

/* Seconds up to a minute, then minutes. A pass is measured in seconds and the
   slow ones are the interesting ones, so "179s" reads better than "3m". */
const took = (ms: number): string => (ms < 60_000 ? `${Math.round(ms / 1000)}s` : `${Math.round(ms / 60_000)}m`);

const cardState = (
  status: "loading" | "ready" | "error",
  rows: ReadonlyArray<TenantPluginUsage>,
): ChartCardState => {
  if (status === "loading") return "loading";
  if (status === "error") return "error";
  if (rows.length === 0) return "empty";
  return "ready";
};

const LastPass = ({ plugin }: { plugin: TenantPluginUsage }): JSX.Element => {
  const tone = lastPassTone(plugin);
  if (plugin.lastPass === null || tone === "never") return <span className={styles.muted}>never</span>;
  const note = LAST_PASS_NOTE[tone];
  const at = formatDateTime(plugin.lastPass.at);
  const feature = plugin.lastPass.feature;
  return (
    <span
      className={note === null ? styles.ok : styles.bad}
      title={
        [at, feature === null ? null : feature, LAST_PASS_TITLE[tone]]
          .filter((part): part is string => part !== null)
          .join(" - ")
      }
    >
      {formatRelativeTime(plugin.lastPass.at)}
      {note === null ? "" : ` - ${note}`}
    </span>
  );
};

/**
 * The passes themselves, newest first, with the window's totals underneath.
 *
 * A total cannot say WHICH pass cost that or whether it failed, and on this
 * tenant one 179-second pass burned a fifth of the window while the summary
 * looked like any other. The list is capped by the box, so the totals under it
 * deliberately describe MORE passes than are listed rather than summing the
 * rows above - the caption says so, because a reader adding up the visible rows
 * and landing short would otherwise read it as an arithmetic bug.
 */
const PassRows = ({ plugin }: { plugin: TenantPluginUsage }): JSX.Element => {
  if (plugin.recentPasses.length === 0) {
    return (
      <p className={styles.muted} title="CloudWatch stores usage as aggregated series, so it can say what a window cost but not which pass cost it.">
        Per-pass detail is not available for this instance.
      </p>
    );
  }
  const listed = plugin.recentPasses.length;
  return (
    <>
      <table className={styles.inner}>
        <thead>
          <tr>
            <th>When</th>
            <th>Feature</th>
            <th className={styles.num}>Tokens in</th>
            <th className={styles.num}>Tokens out</th>
            <th className={styles.num}>Took</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {plugin.recentPasses.map((pass) => (
            <tr key={pass.passId}>
              <td className={styles.muted} title={pass.at}>
                {when(pass.at)}
              </td>
              <td>
                {pass.attributed ? (
                  pass.feature
                ) : (
                  <span className={styles.muted} title="No trigger intent lined up with this pass, so it is not attributed to a feature.">
                    unattributed
                  </span>
                )}
              </td>
              <td className={styles.num} title={exact.format(pass.inputTokens)}>
                {tokens(pass.inputTokens)}
              </td>
              <td className={styles.num} title={exact.format(pass.outputTokens)}>
                {tokens(pass.outputTokens)}
              </td>
              <td className={styles.num}>{took(pass.durationMs)}</td>
              <td>
                {pass.status === "error" ? (
                  <span className={styles.bad}>failed</span>
                ) : (
                  <span className={styles.muted}>ok</span>
                )}
              </td>
            </tr>
          ))}
          <tr className={styles.totalRow}>
            <td colSpan={2}>
              All {exact.format(plugin.passes)} passes in this window
              {listed < plugin.passes && ` (${listed} shown)`}
            </td>
            <td className={styles.num} title={exact.format(plugin.inputTokens)}>
              {tokens(plugin.inputTokens)}
            </td>
            <td className={styles.num} title={exact.format(plugin.outputTokens)}>
              {tokens(plugin.outputTokens)}
            </td>
            <td className={styles.num} />
            <td>
              {plugin.errors > 0 ? (
                <span className={styles.bad}>{exact.format(plugin.errors)} failed</span>
              ) : (
                <span className={styles.muted}>none failed</span>
              )}
            </td>
          </tr>
        </tbody>
      </table>
    </>
  );
};

const FeatureRows = ({ features }: { features: ReadonlyArray<PluginFeatureUsage> }): JSX.Element => (
  <table className={styles.inner}>
    <thead>
      <tr>
        <th>Feature</th>
        <th className={styles.num}>Passes</th>
        <th className={styles.num}>Tokens in</th>
        <th className={styles.num}>Tokens out</th>
        <th className={styles.num}>Failed</th>
      </tr>
    </thead>
    <tbody>
      {features.map((f) => (
        <tr key={f.feature}>
          <td>
            {f.feature}
            {/* Passes whose trigger named no work. Shown rather than folded
                into a feature, so the totals always add up. */}
            {f.feature === "unattributed" && (
              <span className={styles.muted}> - trigger named no work</span>
            )}
          </td>
          <td className={styles.num}>{exact.format(f.passes)}</td>
          <td className={styles.num} title={exact.format(f.inputTokens)}>{tokens(f.inputTokens)}</td>
          <td className={styles.num} title={exact.format(f.outputTokens)}>{tokens(f.outputTokens)}</td>
          <td className={f.errors > 0 ? `${styles.num} ${styles.bad}` : styles.num}>
            {exact.format(f.errors)}
          </td>
        </tr>
      ))}
    </tbody>
  </table>
);

/**
 * Token usage per plugin, and per feature inside it.
 *
 * Rendered only for plugins this tenant actually runs, so an instance with none
 * gets no panel rather than an empty card. A row is every feature SUMMED over
 * the selected range - "Top feature" names the largest contributor and how many
 * the sum spans, so the totals are not read as that one feature's; the row
 * expands for the full breakdown.
 */
export const PluginUsagePanel = ({ tenantId, range }: PluginUsagePanelProps): JSX.Element | null => {
  const { status, plugins, error } = useInstancePluginUsage(tenantId, range);
  const [expanded, setExpanded] = useState<string | null>(null);

  // Nothing installed that reports usage: leave the section out entirely.
  if (status === "ready" && plugins.length === 0) return null;

  const toggle = (pluginId: string): void => setExpanded((current) => (current === pluginId ? null : pluginId));

  return (
    <ChartCard
      title="Plugins"
      subtitle={`Totals per plugin, last ${rangeShortLabel(range)}. Click a row for its features.`}
      hint="A row sums every feature over the selected range. Per-feature splits are attributed, not measured: a pass's tokens are filed under the feature its trigger named."
      wide
      state={cardState(status, plugins)}
      emptyText="No plugins reporting usage on this instance."
      errorText={error ?? undefined}
    >
      <div className={styles.scroll}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Plugin</th>
            <th>Version</th>
            <th className={styles.num}>Passes</th>
            <th className={styles.num}>Tokens in</th>
            <th className={styles.num}>Tokens out</th>
            <th>Top feature</th>
            <th>Last pass</th>
          </tr>
        </thead>
        <tbody>
          {plugins.map((plugin) => (
            <Fragment key={plugin.pluginId}>
              <tr
                className={styles.row}
                onClick={() => toggle(plugin.pluginId)}
                aria-expanded={expanded === plugin.pluginId}
              >
                <td>
                  <span className={styles.caret}>{expanded === plugin.pluginId ? "-" : "+"}</span>
                  {plugin.name}
                </td>
                <td className={styles.muted}>{plugin.version ?? "unknown"}</td>
                <td className={styles.num}>
                  {exact.format(plugin.passes)}
                  {plugin.errors > 0 && (
                    <span className={styles.bad} title={`${plugin.errors} pass(es) failed`}>
                      {" "}
                      ({exact.format(plugin.errors)} failed)
                    </span>
                  )}
                </td>
                <td className={styles.num} title={exact.format(plugin.inputTokens)}>
                  {tokens(plugin.inputTokens)}
                </td>
                <td className={styles.num} title={exact.format(plugin.outputTokens)}>
                  {tokens(plugin.outputTokens)}
                </td>
                <td>
                  {(() => {
                    const top = topFeature(plugin);
                    if (top === null) return <span className={styles.muted}>-</span>;
                    return (
                      <>
                        {top.name}
                        {top.ofTotal !== null && (
                          <span className={styles.muted} title="These totals span every feature, not just this one.">
                            {" "}
                            of {top.ofTotal}
                          </span>
                        )}
                      </>
                    );
                  })()}
                </td>
                <td>
                  <LastPass plugin={plugin} />
                </td>
              </tr>
              {expanded === plugin.pluginId && (
                <tr>
                  <td colSpan={7} className={styles.detail}>
                    {plugin.features.length > 0 ? (
                      <>
                        <PassRows plugin={plugin} />
                        <FeatureRows features={plugin.features} />
                      </>
                    ) : (
                      <p className={styles.muted}>{emptyReason(rangeWindowLabel(range))}</p>
                    )}
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
      </div>
    </ChartCard>
  );
};
